import { useEffect, useReducer } from 'react';

import { TabValue } from '@/types';

import type { RefuelsResult } from '../types/RefuelsResult.types';

type UseRefuelsMobileNavigationParams = {
  result: RefuelsResult | undefined;
  isFetching: boolean;
  isSuccess: boolean;
};

type RefuelsMobileNavigationState = {
  tab: TabValue;
  hasSubmittedDates: boolean;
};

type RefuelsMobileNavigationAction =
  | { type: 'tabChanged'; tab: TabValue }
  | { type: 'targetSelected' }
  | { type: 'datesSubmitted' }
  | { type: 'newVehicleRequested' }
  | { type: 'newDateRequested' }
  | { type: 'resultsArrived' };

const initialState: RefuelsMobileNavigationState = {
  tab: TabValue.targetSelect,
  hasSubmittedDates: false,
};

function refuelsMobileNavigationReducer(
  state: RefuelsMobileNavigationState,
  action: RefuelsMobileNavigationAction
): RefuelsMobileNavigationState {
  switch (action.type) {
    case 'tabChanged':
      return { tab: action.tab, hasSubmittedDates: false };
    case 'targetSelected':
    case 'newDateRequested':
      return { tab: TabValue.dateSelect, hasSubmittedDates: false };
    case 'newVehicleRequested':
      return initialState;
    case 'datesSubmitted':
      return { ...state, hasSubmittedDates: true };
    case 'resultsArrived':
      return { tab: TabValue.result, hasSubmittedDates: false };
    default:
      return action satisfies never;
  }
}

export function useRefuelsMobileNavigation({ result, isFetching, isSuccess }: UseRefuelsMobileNavigationParams) {
  const [state, dispatch] = useReducer(refuelsMobileNavigationReducer, initialState);
  const { tab, hasSubmittedDates } = state;
  const refuelCount = result?.refuels.length ?? 0;

  // This transition records a completed submitted request, so the result tab is not derived from result availability.
  useEffect(() => {
    if (tab !== TabValue.dateSelect || !hasSubmittedDates || isFetching || !isSuccess || refuelCount === 0) {
      return;
    }

    dispatch({ type: 'resultsArrived' });
  }, [hasSubmittedDates, isFetching, isSuccess, refuelCount, tab]);

  return {
    tab,
    hasSubmittedDates,
    changeTab: (nextTab: TabValue) => dispatch({ type: 'tabChanged', tab: nextTab }),
    onTargetSelected: () => dispatch({ type: 'targetSelected' }),
    onDatesSubmitted: () => dispatch({ type: 'datesSubmitted' }),
    onNewVehicle: () => dispatch({ type: 'newVehicleRequested' }),
    onNewDate: () => dispatch({ type: 'newDateRequested' }),
  };
}
